import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Deck } from "@/types/linguadeck";
import { useAuth } from "@/contexts/AuthContext";
import { useAppState } from "@/hooks/useAppState";
import { fetchDeckById } from "@/lib/db";

/**
 * Duplicate (deep copy) the given module/deck:
 * 1. Fetch latest module + cards from DB
 * 2. Create new deck with title + copy_suffix; clone all cards with new deck id and fresh IDs
 * 3. Update local library state and show success toast
 * 4. On error, show error toast
 */
export function useModuleDuplicate(module: Deck | null) {
  const { user } = useAuth();
  const { addDeck } = useAppState();
  const { t } = useTranslation();
  const [isDuplicating, setIsDuplicating] = useState(false);

  const handleDuplicate = useCallback(async () => {
    if (!module?.id || !user) return;

    setIsDuplicating(true);
    try {
      // 1. Fetch original data (module details + all cards)
      const original = await fetchDeckById(module.id);
      if (!original) {
        toast.error(t("module_duplicated_error", "Не удалось загрузить набор"));
        return;
      }

      const copySuffix = t("copy_suffix", "(Копия)");
      const newTitle = `${original.title}${copySuffix}`;

      // 2. Deep copy: new deck (no id → DB generates), cloned cards (no ids → createDeck assigns new deck_id and new card ids)
      const copy = {
        ...original,
        title: newTitle,
        cards: [...(original.cards || [])],
      };
      // Omit id so createDeck generates a new one; cards get new ids and new deck_id inside createDeck
      const { id: _omit, ...payload } = copy;

      await addDeck(payload as Deck);

      // 3. State is updated by addDeck; show success toast
      toast.success(t("module_duplicated_success", "Набор успешно дублирован"));
    } catch (err) {
      console.error("[ModuleActions] Duplicate failed:", err);
      toast.error(t("module_duplicated_error", "Не удалось дублировать набор"));
    } finally {
      setIsDuplicating(false);
    }
  }, [module?.id, user, addDeck, t]);

  return { handleDuplicate, isDuplicating };
}

/**
 * Print: opens the browser's native print dialog.
 * Relies on @media print CSS to hide UI and show deck title + cards.
 */
export function useModulePrint(module: Deck | null) {
  const handlePrint = useCallback(() => {
    if (!module) return;
    window.print();
  }, [module?.id]);
  return { handlePrint };
}
